import {createContext, useContext, useState} from 'react'
import {useSelector} from 'react-redux'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getAlternativeOffersQueryParams} from 'modules/alternativeOffers/selectors'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getSubBrandKeyFromUrl} from '@vio/core/src/_web/brand/utils'
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook -- needed to derive URL subbrand from current brand code.
import {getBrandByCode} from '@vio/core/src/brand/config'
import {useLazyGetOfferCheckQuery} from '@vio/core/src/offer/services/offerCheckApi'
import {
  OfferCheckApiRequestParams,
  OfferCheckAPIResponse
} from '@vio/core/src/offer/types/OfferCheck'
import {useLazyGetOffersQuery} from '@vio/core/src/sapi/services/searchApi'

type triggerOfferCheckParams = {
  apiParams: OfferCheckApiRequestParams
  offerId: string
  onSuccess?: ({
    httpRequestId,
    offerCheck
  }: {
    httpRequestId: string
    offerCheck: OfferCheckAPIResponse
  }) => void
}

type OfferCheckContextType = {
  triggerOfferCheck: (params: triggerOfferCheckParams) => void
  isUserInitiatedCheckPending: boolean
}

const OfferCheckContext = createContext<OfferCheckContextType>({
  triggerOfferCheck: undefined,
  isUserInitiatedCheckPending: false
})

export const useOfferCheckContext = () => useContext(OfferCheckContext)

export const OfferCheckContextProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const offersQueryParams = useSelector(state => {
    const hotelId = getAccommodationPageHotelId(state)
    return getAlternativeOffersQueryParams(state, hotelId)
  })

  const [fetchAllOffers] = useLazyGetOffersQuery()
  const [fetchOfferCheck] = useLazyGetOfferCheckQuery()

  const {brandCode} = useBrand()
  const brand = getBrandByCode(brandCode)
  const subBrandCode = getSubBrandKeyFromUrl(brand)

  const [isUserInitiatedCheckPending, setIsUserInitiatedCheckPending] =
    useState(false)

  const triggerOfferCheck: OfferCheckContextType['triggerOfferCheck'] = async ({
    apiParams,
    offerId,
    onSuccess
  }) => {
    /**
     * A proactive check can already be in-flight when the user clicks Book.
     * RTK Query deduplicates the request (same cache key) and condition-rejects
     * the user's call, but .unwrap() still waits for the shared in-flight
     * request to settle — so onSuccess fires correctly in both cases.
     */
    setIsUserInitiatedCheckPending(true)

    fetchOfferCheck({
      ...apiParams,
      options: {
        offerId,
        subBrandCode
      }
    })

    fetchAllOffers(offersQueryParams, true)

    try {
      const result = await fetchOfferCheck({
        ...apiParams,
        options: {
          offerId,
          subBrandCode
        }
      }).unwrap()
      onSuccess?.({httpRequestId: result.httpRequestId, offerCheck: result})
    } catch {
      // Offer check failed; sold-out handling is done in accommodationMiddleware
    } finally {
      setIsUserInitiatedCheckPending(false)
    }
  }

  return (
    <OfferCheckContext.Provider
      value={{triggerOfferCheck, isUserInitiatedCheckPending}}
    >
      {children}
    </OfferCheckContext.Provider>
  )
}
